import { Injectable, NgZone, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { UserState } from './user.state';
import { PermissionsService } from './permissions.service';

export interface RoleGuardData {
  resource: PermissionResource;
  actions?: PermissionAction[];
}

/**
 * @deprecated please use {@link canActivateRoleGuard}
 */
@Injectable({
  providedIn: 'root'
})
export class RoleGuard {
  private readonly router = inject(Router);
  protected userState = inject(UserState);
  private readonly ngZone = inject(NgZone);
  private readonly toast = inject(ToastrService);
  private readonly permissionsService = inject(PermissionsService);

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.accessBasedOnPermissions(route);
  }

  private accessBasedOnPermissions(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const allowedResources: RoleGuardData[] = route.data?.allowedResources || [];

    return combineLatest([this.userState.accessViews$, this.userState.allPermissions$]).pipe(
      filter(([access, permissions]) => !!access && !!permissions),
      map(() => this.userRolesAllowed(allowedResources)),
      map(result => (typeof result === 'string' ? this.router.parseUrl(result) : result)),
      tap(
        result =>
          !result && this.toast.error('Not authorized for page.') && this.ngZone.run(() => this.router.navigate(['']))
      )
    );
  }

  private userRolesAllowed(requiredResources: RoleGuardData[]): boolean {
    if (this.permissionsService.isSuperAdmin()) {
      return true;
    }

    const minRequirement = this.permissionsService.hasPermissions(PermissionResource.CustomerApp);
    if (!minRequirement) {
      return false;
    }

    if (!requiredResources?.length) return true;

    // check allowed actions
    return requiredResources.some(resource =>
      this.permissionsService.hasPermissionsAndActions({ resources: [resource.resource], actions: resource.actions })
    );
  }
}
